import { mat4 } from 'gl-matrix';

/**
 * When working with medical data, we often times work in many different 3D spaces.
 *
 * There are image spaces of different kinds
 * and a single PatientSpace that should be common between all the data.
 *
 * To convert point from one of these spaces to any other space, we perform an operation called Transformation.
 * Transformation is a vector - matrix multiplication, which converts the coordinate data from one space to another.
 * This multiplication and creation of Transformation matrices are handled by Matrix-classes.
 *
 * However, sometimes we need to combine transformations.
 * Say we have two scans, scanA and scanB. We also have a PatientSpace that is common between these two.
 * We don't know the direct relation between these two scans.
 * We only know scanA's transformation relative to PatientSpace and scanB's transformation relative to PatientSpace.
 *
 * In order for us to convert point in scanA to point in scanB, we must perform two transformations;
 * spaceA -> PatientSpace and PatientSpace -> spaceB.
 * Transformation A presents the transformation from SpaceA to PatientSpace.
 * Transformation B presents the transformation from SpaceB to PatientSpace.
 *
 * For our convenience, 4 by 4 matrice's inverse reverses the direction of the operation.
 * So we have formula V * T = V', where V is vertex, T is a 4 by 4 transformationMatrix and V' is transformed vertex.
 * Then V' * T' = V, where T' is inverse of T.
 *
 * Meaning that if we know the Transformation B converts data from SpaceB to PatientSpace,
 * B' will convert data from PatientSpace to SpaceB.
 *
 * Another property of TransformationMatrices is that we can combine transformations using matrix - matrix multiplication.
 * Let's say we have point P with subscript a, patient or b, depending on the space in which that point resides.
 *
 * From what we already know, we can do the following to convert point from SpaceA to spaceB using two vector - matrix multiplications:
 * Pa * A = Ppatient
 * Ppatient * B' = Pb.
 *
 * However using Matrix - Matrix multiplication, we could combine A and B'
 * to convert the data from Pa to Pb directly, using just a single vector - matrix multiplication.
 *
 * So the result becomes
 * B' * A = AB
 * Pa * AB = Pb
 *
 * And we can combine as many of these matrix - matrix multiplications as needed.
 *
 * Transformation-class is designed to provide easy access to those matrix - matrix multiplications,
 * so we can easily convert data from one space to another space knowing only the direct sequence of transformations.
 */
export class Transformation implements Iterable<Transformation> {
    /**
     * The parent transformation. The transformation that should be applied before this one.
     * Undefined if this Transformation has no parent.
     */
    private _parent?: Transformation;

    /**
     * Array of transformations that are dependent on this transformation.
     * The total transformation of each of the children should change whenever the parent's total transformation changes.
     */
    private children: Transformation[];

    /**
     * The total transformation from the top of this transformation sequence all the way down to this point.
     * Undefined if the transformations are identity. Meaning transformation that does not apply any additional transformation.
     */
    private _totalTransformation?: mat4;

    /**
     * Transformation introduced by this Transformation-object.
     * Undefined if the transformation is identity. Meaning transformation that does not apply any additional transformation.
     */
    private ownTransformation?: mat4;

    /**
     * Constructor that will create a new Transformation object
     * @param ownTransformation The initial own transformation
     * @param parent The parent transformation
     */
    constructor(ownTransformation?: mat4, parent?: Transformation) {
        this.children = [];
        this.ownTransformation = ownTransformation;
        if (ownTransformation) {
            this._totalTransformation = mat4.clone(ownTransformation);
        }
        this._parent = parent;
        if (parent) {
            parent.children.push(this);
        }
    }

    /**
     * Method to get the own transformation
     */
    public get localTransformation(): mat4 | undefined {
        return this.ownTransformation;
    }

    /**
     * Method to update the local transformation matrix
     * This triggers total transformation update, which also updates all the children
     * @param transformation The new local transformation
     */
    public set localTransformation(transformation: mat4 | undefined) {
        if (this.ownTransformation) {
            //if transformation is identity, it can just be removed as it would not alter the result
            if (transformation && !mat4.equals(transformation, mat4.create())) {
                mat4.copy(this.ownTransformation, transformation);
            } else {
                this.ownTransformation = undefined;
            }
        } else if (transformation) {
            this.ownTransformation = mat4.clone(transformation);
        } else {
            //if both ownTransformation and new transformation are undefined, do nothing
            return;
        }
        this.updateTotalTransformation();
    }

    /**
     * Method to get the total transformation from the first parent all the way down to this point, including own transformation.
     * Undefined if the transformation is identity and does not change the vertex data.
     */
    public get totalTransformation(): mat4 | undefined {
        return this._totalTransformation;
    }

    /**
     * Method to update the total transformation of the transformation.
     * Multiplying own transformation with parent transformation.
     * @returns reference to this
     */
    protected updateTotalTransformation(): Transformation {
        let total: mat4 | undefined = undefined;
        if (this.ownTransformation) {
            total = mat4.clone(this.ownTransformation);
        }
        if (this.parent) {
            const transformation = this.parent.totalTransformation;
            if (transformation) {
                if (total) {
                    mat4.mul(total, total, transformation);
                } else {
                    total = mat4.clone(transformation);
                }
            }
        }
        this._totalTransformation = total;
        for (const child of this) {
            child.updateTotalTransformation();
        }
        return this;
    }

    /**
     * Method to add children to Transformation.
     * Creates the references and updates the total transformation of the child.
     * @param children Transformations to be added as children to this Transformation
     * @returns Reference to this
     */
    public addChildren(...children: Transformation[]): Transformation {
        for (const child of children) {
            child.parent = this;
            this.children.push(child);
            child.updateTotalTransformation();
        }
        return this;
    }

    /**
     * Method to set parent of this Transformation.
     * Updates the old parent accordingly as well.
     * @param parent The new parent or undefined to remove parent completely
     */
    public set parent(parent: Transformation | undefined) {
        if (this._parent) {
            const indexInOldParent = this._parent.children.indexOf(this);
            if (indexInOldParent > -1) {
                this._parent.children.splice(indexInOldParent, 1);
            }
        }
        if (parent) {
            parent.addChildren(this);
        }
        this.updateTotalTransformation();
    }

    /**
     * Method to get {@link Iterator} over all the child Transformations.
     * @returns iterator over the {@link children}-array.
     */
    [Symbol.iterator](): Iterator<Transformation> {
        let index = 0;
        return {
            next: (): IteratorResult<Transformation> => {
                if (index < this.children.length) {
                    return { value: this.children[index++], done: false };
                } else {
                    return { value: undefined, done: true };
                }
            },
        };
    }
}

/**
 * Interface for objects that may be transformed.
 */
export interface Transformable {
    /**
     * Method to get the {@link Transformation}.
     * @returns The current {@link Transformation} or undefined if there's currently no active {@link Transformation}.
     */
    get transformation(): Transformation | undefined;
}
